/**
 * Consult Scheduling — ADMIN reschedule/cancel SEAM.
 *
 * The staff-facing counterpart to the public Door B booking seam. Talks to
 * Mack's TEAM-AUTHENTICATED callables (no App Check — the caller is a signed-in
 * staff session):
 *
 *   rescheduleConsultation({ consultationId, newSlotStartISO })
 *     → { ok, newConsultationId, status, meetUrl, rescheduleUrl?, reason? }
 *       | { ok:false, status:'slot_taken' }
 *   cancelConsultation({ consultationId })
 *     → { ok, status:'cancelled' }
 *
 * These are NOT deployed yet, so the live provider fails today and the UI shows
 * the HONEST loading/error state — never a fake success. The mock provider (QA /
 * `?preview=1` / the harness) lets Vera walk the flows with no backend. The one
 * clean interface means the deploy is a config flip, not a code change.
 */

/** `rescheduleConsultation` outcome. */
export type AdminRescheduleResult =
  | {
      kind: 'ok';
      newConsultationId: string;
      status: 'confirmed' | 'pending';
      meetUrl: string | null;
    }
  | { kind: 'slot_taken' }
  | { kind: 'error'; message: string };

/** `cancelConsultation` outcome. */
export type AdminCancelResult =
  | { kind: 'ok' }
  | { kind: 'error'; message: string };

/** The seam the admin UI depends on — identical shape for live + mock. */
export interface ConsultAdminApi {
  reschedule(consultationId: string, newSlotStartISO: string): Promise<AdminRescheduleResult>;
  cancel(consultationId: string): Promise<AdminCancelResult>;
  /** true for the mock — the admin surface shows a slim "sample data" ribbon. */
  readonly isPreview: boolean;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

async function callTeamCallable<TIn extends object, TOut>(
  name: string,
  payload: TIn,
): Promise<TOut> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('../../../lib/firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<TIn, TOut>(functions, name);
  return (await fn(payload)).data;
}

export const liveConsultAdminApi: ConsultAdminApi = {
  isPreview: false,

  async reschedule(consultationId, newSlotStartISO) {
    try {
      const data = await callTeamCallable<
        { consultationId: string; newSlotStartISO: string },
        Record<string, unknown>
      >('rescheduleConsultation', { consultationId, newSlotStartISO });
      if (data.status === 'slot_taken') return { kind: 'slot_taken' };
      if (data.ok === true) {
        return {
          kind: 'ok',
          newConsultationId:
            asString(data.newConsultationId) ?? asString(data.consultationId) ?? consultationId,
          status: data.status === 'pending' ? 'pending' : 'confirmed',
          meetUrl: asString(data.meetUrl),
        };
      }
      return {
        kind: 'error',
        message:
          asString(data.reason) ??
          "We couldn't move this call. The original time is unchanged.",
      };
    } catch {
      // Not deployed / offline / not a staff session → honest failure.
      return {
        kind: 'error',
        message:
          "Couldn't reach the scheduling service. Nothing changed — please try again in a moment.",
      };
    }
  },

  async cancel(consultationId) {
    try {
      const data = await callTeamCallable<
        { consultationId: string },
        Record<string, unknown>
      >('cancelConsultation', { consultationId });
      if (data.ok === true && data.status === 'cancelled') return { kind: 'ok' };
      return {
        kind: 'error',
        message: asString(data.reason) ?? "We couldn't cancel this call — please try again.",
      };
    } catch {
      return {
        kind: 'error',
        message: "Couldn't reach the scheduling service — please try again in a moment.",
      };
    }
  },
};

/** Mock provider (QA / preview only) — never used on the real team route. */
export function makeMockConsultAdminApi(): ConsultAdminApi {
  return {
    isPreview: true,
    async reschedule(_consultationId, _newSlotStartISO) {
      await new Promise((r) => setTimeout(r, 260));
      return {
        kind: 'ok',
        newConsultationId: 'CS-PREVIEW-RESCHED',
        status: 'confirmed',
        meetUrl: 'https://meet.google.com/aayatan-preview',
      };
    },
    async cancel(_consultationId) {
      await new Promise((r) => setTimeout(r, 220));
      return { kind: 'ok' };
    },
  };
}
